import FolderIcon from '@mui/icons-material/Folder';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import type { DashboardFolderTreeNodeT } from 'business/definitions';
import Loading from 'components/Loading';
import { useIsAdminOrSupportUser } from 'hooks/user';
import type { SyntheticEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';

import { buildDashboardFolderTree } from '../../helpers/folderTree';
import { findFolderPath } from '../../helpers/recursiveFoldersHelpers';
import { useGetDashboardFolders } from '../../hooks/dashboardFolders';

const StyTreeItem = styled(TreeItem, {
    name: 'StyTreeItem',
})(({ theme }) => ({
    '.MuiTreeItem-content': {
        '&.Mui-selected': {
            borderColor: theme.palette.primary.main,
            borderRadius: theme.spacing(0.5),
            borderStyle: 'solid',
            borderWidth: theme.spacing(0.25),
        },
    },
}));

type Props = {
    initialFolderId?: number | null;
    excludedFolderId?: number | null;
    selectedFolderId: number | null;
    setSelectedFolderId: (id: number | null) => void;
};

const DashboardFolderSelectionTree = ({
    selectedFolderId,
    setSelectedFolderId,
    initialFolderId = null,
    excludedFolderId = null,
}: Props): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isAdminOrSupportUser = useIsAdminOrSupportUser();

    const {
        data: dashboardFolders = [],
        isLoading: dashboardFoldersIsLoading,
    } = useGetDashboardFolders();

    const dashboardsFolderTree = useMemo(
        () =>
            buildDashboardFolderTree(
                dashboardFolders,
                [],
                true,
                excludedFolderId,
            ),
        [dashboardFolders, excludedFolderId],
    );
    const initialExpanded = useMemo(() => {
        if (!initialFolderId) return ['null'];
        const path = findFolderPath(
            dashboardsFolderTree.folders,
            initialFolderId,
            ['null'],
            (folder) => folder.id.toString(),
        );
        // We remove the last element (which is the dashboard folder itself)
        return path ? path.slice(0, -1) : ['null'];
    }, [dashboardsFolderTree, initialFolderId]);

    const [expanded, setExpanded] = useState<string[]>(initialExpanded);

    // Only users with admin or support roles can interact with folders.
    if (!isAdminOrSupportUser) {
        setSelectedFolderId(null);
        return <></>;
    }

    const handleSelectedItemsChange = (
        _: SyntheticEvent,
        nodeId: string | null,
    ) => {
        setSelectedFolderId(
            nodeId === 'null' || nodeId === null ? null : Number(nodeId),
        );
    };

    const renderFolders = (folders: DashboardFolderTreeNodeT[]) =>
        folders.map((folder) => (
            <StyTreeItem
                key={folder.id}
                itemId={folder.id.toString()}
                label={
                    <Flex align="center" gap={1}>
                        <FolderIcon
                            fontSize="small"
                            sx={{ color: theme.palette.mediumGrey }}
                        />
                        <Typography variant="body1">{folder.name}</Typography>
                    </Flex>
                }
            >
                {folder.folders &&
                    folder.folders.length > 0 &&
                    renderFolders(folder.folders)}
            </StyTreeItem>
        ));

    return (
        <Box sx={{ width: '100%' }}>
            {dashboardFoldersIsLoading ? (
                <Loading />
            ) : (
                <Grid container spacing={2}>
                    <SimpleTreeView
                        selectedItems={selectedFolderId?.toString() ?? 'null'}
                        onSelectedItemsChange={handleSelectedItemsChange}
                        expandedItems={expanded}
                        onExpandedItemsChange={(_, nodeIds) =>
                            setExpanded(nodeIds)
                        }
                    >
                        <StyTreeItem
                            itemId="null"
                            label={t('dashboards.breadcrumbsRoot')}
                        >
                            {renderFolders(dashboardsFolderTree.folders)}
                        </StyTreeItem>
                    </SimpleTreeView>
                </Grid>
            )}
        </Box>
    );
};

export default DashboardFolderSelectionTree;
